import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  Radio,
  RadioGroup,
  Tooltip,
  makeStyles,
} from "@fluentui/react-components";
import { ReOrderDotsVertical20Regular } from "@fluentui/react-icons";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelectedTag } from "../../contexts/SelectedTagContext";
import { useTagsRearrange, useTagsSort } from "../../hooks/useTags";
import {
  FlatVsaaTag,
  RearrangeTagRequest,
  SortTagRequest,
  TagSortMode,
} from "../../models/VsaaTag";
import { SkanskaColorTokens } from "../../style/colors";

const useStyles = makeStyles({
  sortPanelContainer: {
    display: "flex",
    flexDirection: "column",
  },
  sortTitle: {
    marginBottom: "10px",
  },
  moveTagsContainer: {
    backgroundColor: SkanskaColorTokens.color_primary_gray,
  },
  saveSortButton: {
    marginTop: "16px",
  },
  draggableItem: {
    padding: "10px",
    background: "white",
    borderRadius: "4px",
    border: "1px solid #ddd",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "grab",
    overflowY: "auto",
  },
  draggableText: {
    paddingLeft: "10px",
    flex: "1",
  },
  draggableContainer: {
    background: SkanskaColorTokens.color_primary_gray,
    padding: "10px",
    borderRadius: "5px",
    minHeight: "100px",
    width: "600px",
    maxHeight: "300px",
    overflowY: "auto",
  },
  sortButtons: {
    display: "flex",
    gap: "10px",
    marginTop: "10px",
  },
});

type SortTagsPanelProps = {
  tagCollectionId: string;
  tags: FlatVsaaTag[];
  onIsSorting: (isSorting: boolean) => void;
};

const getChildren = (
  tag: FlatVsaaTag | undefined,
  allTags: FlatVsaaTag[]
): FlatVsaaTag[] => {
  return allTags.filter((t) => t.parentId === tag?.id);
};

const SortableItem = ({
  id,
  title,
  path,
}: {
  id: string;
  title: string;
  path: string;
  position: number;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const classes = useStyles();

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={classes.draggableItem}
    >
      <ReOrderDotsVertical20Regular />
      <Tooltip content={path} relationship="label">
        <span className={classes.draggableText}>{title}</span>
      </Tooltip>
    </div>
  );
};

const SortTagsPanel = ({
  tagCollectionId,
  tags,
  onIsSorting,
}: SortTagsPanelProps) => {
  const classes = useStyles();
  const { mutateAsync: sortTag, isPending: sortPending } =
    useTagsSort(tagCollectionId);
  const { mutateAsync: rearrangeTag, isPending: rearrangeTagPending } =
    useTagsRearrange(tagCollectionId);

  const [selectedModeState, setSelectedModeState] = useState<
    { tagId: string; mode: string } | undefined
  >(undefined);
  const [manualSortState, setManualSortState] = useState<
    { tagId: string; childIds: string[]; tagsMoved: boolean } | undefined
  >(undefined);
  const { t } = useTranslation();

  const tagContext = useSelectedTag();
  const selectedTag = tagContext?.selectedTag;
  const selectedTagId = selectedTag?.id;
  const savedMode =
    selectedTag?.sortMode === TagSortMode.Automatic ? "ascending" : "manual";
  const selectedMode =
    selectedModeState && selectedModeState.tagId === selectedTagId
      ? selectedModeState.mode
      : savedMode;

  const children = useMemo(() => {
    const currentChildren = getChildren(selectedTag, tags);

    if (!selectedTagId || manualSortState?.tagId !== selectedTagId) {
      return currentChildren;
    }

    const childrenById = new Map(
      currentChildren.map((child) => [child.id, child])
    );
    const sortedChildren = manualSortState.childIds
      .map((childId) => childrenById.get(childId))
      .filter((child): child is FlatVsaaTag => Boolean(child));
    const sortedChildIds = new Set(sortedChildren.map((child) => child.id));
    const newChildren = currentChildren.filter(
      (child) => !sortedChildIds.has(child.id)
    );

    return [...sortedChildren, ...newChildren];
  }, [manualSortState, selectedTag, selectedTagId, tags]);

  const tagsMoved =
    manualSortState && manualSortState.tagId === selectedTagId
      ? manualSortState.tagsMoved
      : false;
  const hasModeChanged = selectedMode !== savedMode;
  const hasManualOrderChanged = selectedMode === "manual" && tagsMoved;
  const hasUnsavedChanges = hasModeChanged || hasManualOrderChanged;

  const handleSelectedModeChange = (mode: string) => {
    if (!selectedTagId) {
      return;
    }

    setSelectedModeState({ tagId: selectedTagId, mode });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = children.findIndex((child) => child.id === active.id);
      const newIndex = children.findIndex((child) => child.id === over.id);
      const reorderedChildren = arrayMove(children, oldIndex, newIndex);

      if (selectedTagId) {
        setManualSortState({
          tagId: selectedTagId,
          childIds: reorderedChildren.map((child) => child.id),
          tagsMoved: true,
        });
      }
    }
  };

  const handleSave = async () => {
    if (!selectedTag) {
      return;
    }

    if (selectedMode === "ascending") {
      const sortAscRequest: SortTagRequest = {
        id: selectedTag.id,
        ancestorIds: selectedTag.ancestorIds,
        sortAscending: true,
      };
      await sortTag(sortAscRequest);
    } else if (selectedMode === "manual") {
      const fromAncestorIds = selectedTag.ancestorIds?.length
        ? [...selectedTag.ancestorIds, selectedTag.id]
        : [selectedTag.id];

      const targetAncestorIds = [...fromAncestorIds];

      const rearrangeRequest: RearrangeTagRequest = {
        tags: children.map((tag, i) => ({
          id: tag.id,
          targetPosition: i,
        })),
        fromAncestorIds,
        targetAncestorIds,
      };

      onIsSorting(true);
      await rearrangeTag(rearrangeRequest);
      onIsSorting(false);
    }
  };

  const handleCancel = () => {
    setSelectedModeState(undefined);
    setManualSortState(undefined);
  };

  return (
    <div className={classes.sortPanelContainer}>
      <h3 className={classes.sortTitle}>{t("sort")}</h3>
      <RadioGroup
        value={selectedMode || ""}
        onChange={(_, data) => handleSelectedModeChange(data.value)}
      >
        <Radio value="ascending" label={t("alphabeticalOrder")} />
        <Radio value="manual" label={t("manualSort")} />
      </RadioGroup>

      {selectedMode === "manual" && (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          {tagContext?.selectedTag?.hasChildren && (
            <>
              <SortableContext
                items={children.map((child) => child.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className={classes.draggableContainer}>
                  {children.map((child, index) => (
                    <SortableItem
                      key={child.id}
                      id={child.id}
                      title={child.title}
                      path={child.path}
                      position={index + 1}
                    />
                  ))}
                </div>
              </SortableContext>
            </>
          )}
        </DndContext>
      )}

      {!tagContext?.selectedTag?.hasChildren && selectedMode ? (
        <p>{t("noChildTags")} </p>
      ) : (
        ""
      )}
      <div className={classes.sortButtons}>
        {selectedMode && (
          <Button onClick={handleCancel} disabled={!hasUnsavedChanges}>
            {t("cancel")}
          </Button>
        )}
        {selectedMode && (
          <Button
            appearance="primary"
            onClick={handleSave}
            disabled={
              sortPending ||
              rearrangeTagPending ||
              !tagContext?.selectedTag?.hasChildren ||
              !hasUnsavedChanges
            }
          >
            {selectedMode === "manual" ? t("saveSort") : t("save")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default SortTagsPanel;
